import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardCheck, AlertTriangle,
  CheckCircle2, Bell, ShieldAlert, LayoutGrid,
} from "lucide-react";
import { Button } from "@/design/components";
import { formatPct } from "@/design/format";
import { useMoney } from "@/domain/hooks";
import { useDealCards, useCrossDealTotals } from "@/screens/shared/dealCards";
import { DealCard } from "@/screens/stratus/DealCard";
import { cn } from "@/design/cn";

export function StratusDashboardHome() {
  const money = useMoney();
  const navigate = useNavigate();
  // The deals needing attention are the cards further down this same page, so the
  // triage chip scrolls to the grid rather than navigating anywhere.
  const activeGridRef = useRef<HTMLElement>(null);
  const cards = useDealCards();
  const totals = useCrossDealTotals();

  const active = cards.filter((c) => c.isActive);
  const preparing = cards.filter((c) => !c.isActive);

  const needYou = active.filter((c) => c.todos.length > 0).length;
  const atRisk = active.filter((c) => (c.health as string) === "red").length;
  const onWatch = active.filter((c) => (c.health as string) === "amber").length;
  const onTrack = active.filter((c) => (c.health as string) === "green").length;

  const totalSpent = cards.reduce((sum, c) => sum + c.spent, 0);
  const totalCommitted = cards.reduce((sum, c) => sum + c.budget, 0);
  const burnPct = totalCommitted > 0 ? totalSpent / totalCommitted : 0;

  const allClear = needYou === 0 && totals.approvals === 0 && totals.signals === 0 && atRisk === 0;

  return (
    <div className="space-y-6">
      {/* ── Portfolio summary header ────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-xl border border-line bg-[#0A0E22]">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.035]"
          // style={{
          //   backgroundImage:
          //     "linear-gradient(rgba(255,255,255,0.9) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.9) 1px,transparent 1px)",
          //   backgroundSize: "32px 32px",
          // }}
        />
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#272FA0] opacity-20 blur-3xl" />

        <div className="relative flex flex-col gap-6 px-6 py-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              {allClear ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-ok" />
              ) : (
                <AlertTriangle className="h-3.5 w-3.5 text-warn" />
              )}
              <span className={cn("text-[11px] font-semibold uppercase tracking-widest", allClear ? "text-ok" : "text-warnStrong")}>
                {allClear
                  ? "Portfolio clear"
                  : needYou > 0
                  ? `${needYou} deal${needYou > 1 ? "s" : ""} need attention`
                  : "Portfolio active"}
              </span>
            </div>
            <div className="mt-3">
              <p className="text-[11px] font-medium uppercase tracking-wider text-white/30">
                {active.length} active deal{active.length !== 1 ? "s" : ""} · deployed to date
              </p>
              <p className="mt-1 font-mono text-4xl font-bold tabular-nums tracking-tight text-white/95">
                {money(totalSpent)}
              </p>
              <p className="mt-1.5 text-sm text-white/35">
                of {money(totalCommitted)} committed · {formatPct(burnPct, 0)} burn
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              {onTrack > 0 && <HealthChip count={onTrack} tone="ok" label="on track" />}
              {onWatch > 0 && <HealthChip count={onWatch} tone="warn" label="watch" />}
              {atRisk > 0 && <HealthChip count={atRisk} tone="bad" label="at risk" />}
            </div>
            <div className="flex items-center gap-2">
              {/* <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate("/workspace/uganda-fire-stations/onboarding")}
                className="border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
              >
                <UploadCloud className="h-3.5 w-3.5" /> AI upload
              </Button> */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate("/stratus/portfolio/site-visits")}
                className="border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
              >
                <ClipboardCheck className="h-3.5 w-3.5" /> Site visit
              </Button>
            </div>
          </div>
        </div>

        {totalCommitted > 0 && (
          <div className="relative px-6 pb-4">
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  burnPct > 0.9 ? "bg-bad" : burnPct > 0.7 ? "bg-warn" : "bg-ok"
                )}
                style={{ width: `${Math.min(burnPct * 100, 100)}%` }}
              />
            </div>
            <p className="mt-1.5 text-[11px] text-white/25">
              Portfolio budget consumed · {formatPct(burnPct, 1)}
            </p>
          </div>
        )}
      </div>

      {/* ── Triage strip ────────────────────────────────────────────────── */}
      {(needYou > 0 || totals.approvals > 0 || totals.signals > 0) && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-warn/30 border-l-4 border-l-warn bg-warnSoft px-4 py-3.5">
          <span className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-warn/25">
            <AlertTriangle className="h-3.5 w-3.5 text-warnStrong" />
          </span>
          <span className="text-sm font-bold text-warnStrong">Action needed</span>
          <span className="h-3 w-px bg-warn/40 mx-1" aria-hidden />
          {needYou > 0 && (
            <TriageChip
              icon={LayoutGrid}
              label={`${needYou} deal${needYou > 1 ? "s" : ""} awaiting`}
              onClick={() =>
                activeGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            />
          )}
          {totals.approvals > 0 && (
            <TriageChip icon={Bell} label={`${totals.approvals} approval${totals.approvals > 1 ? "s" : ""}`} onClick={() => navigate("/stratus/approvals")} />
          )}
          {totals.signals > 0 && (
            <TriageChip icon={ShieldAlert} label={`${totals.signals} signal${totals.signals > 1 ? "s" : ""}`} onClick={() => navigate("/stratus/signals")} />
          )}
        </div>
      )}

      {/* ── Active deal grid ─────────────────────────────────────────────── */}
      {active.length > 0 && (
        <section ref={activeGridRef} className="scroll-mt-24">
          <SectionLabel icon={LayoutGrid} label={`Active · ${active.length}`} />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {active.map((c) => (
              <DealCard key={c.id} data={c} to={`/workspace/${c.workspaceId}`} from="/stratus" />
            ))}
          </div>
        </section>
      )}

      {/* ── Pipeline band ────────────────────────────────────────────────── */}
      {preparing.length > 0 && (
        <section>
          <SectionLabel label={`In pipeline · ${preparing.length}`} muted />
          <div className="flex gap-3 overflow-x-auto pb-2">
            {preparing.map((c) => (
              <div key={c.id} className="w-72 shrink-0">
                <DealCard data={c} to="" disabled />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Portfolio footer ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCell label="Committed" value={money(totalCommitted)} note="total envelope" tone="brand" />
        <StatCell
          label="Deployed"
          value={money(totalSpent)}
          note={`${formatPct(burnPct, 0)} of envelope`}
          tone={burnPct > 0.85 ? "warn" : "ok"}
        />
        <StatCell
          label="Open asks"
          value={String(totals.approvals)}
          note="awaiting approval"
          tone={totals.approvals > 0 ? "warn" : undefined}
        />
        <StatCell
          label="Signals"
          value={String(totals.signals)}
          note="flagged items"
          tone={totals.signals > 0 ? "bad" : undefined}
        />
      </div>
    </div>
  );
}

// ─── Local sub-components ─────────────────────────────────────────────────────

function HealthChip({ count, tone, label }: { count: number; tone: "ok" | "warn" | "bad"; label: string }) {
  const cls =
    tone === "ok"
      ? "border-ok/20 bg-okSoft text-okStrong"
      : tone === "warn"
      ? "border-warn/20 bg-warnSoft text-warnStrong"
      : "border-bad/20 bg-badSoft text-bad";
  const dot = tone === "ok" ? "bg-ok" : tone === "warn" ? "bg-warn" : "bg-bad";
  return (
    <span className={cn("flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold", cls)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      {count} {label}
    </span>
  );
}

function TriageChip({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 rounded-lg border border-warn/25 bg-warn/10 px-2.5 py-1 text-xs font-medium text-warnStrong hover:bg-warn/20 transition-colors"
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

function SectionLabel({
  icon: Icon,
  label,
  muted,
}: {
  icon?: React.ElementType;
  label: string;
  muted?: boolean;
}) {
  return (
    <div className="mb-3 flex items-center gap-2">
      {Icon && <Icon className="h-3.5 w-3.5 text-faint" />}
      <p className={cn("shrink-0 text-[11px] font-semibold uppercase tracking-widest", muted ? "text-faint" : "text-mute")}>
        {label}
      </p>
      <div className="h-px flex-1 bg-hairline" />
    </div>
  );
}

function StatCell({ label, value, note, tone }: {
  label: string;
  value: string;
  note?: string;
  tone?: "ok" | "warn" | "bad" | "brand";
}) {
  const valCls =
    tone === "ok" ? "text-okStrong" :
    tone === "warn" ? "text-warnStrong" :
    tone === "bad" ? "text-bad" :
    tone === "brand" ? "text-brand" :
    "text-ink";
  return (
    <div className="rounded-xl border border-line bg-card px-4 py-3.5">
      <p className="text-[11px] font-medium uppercase tracking-widest text-faint">{label}</p>
      <p className={cn("mt-1.5 text-xl font-bold tabular-nums tracking-tight", valCls)}>{value}</p>
      {note && <p className="mt-0.5 text-[11px] text-faint">{note}</p>}
    </div>
  );
}
